import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight, MapPin } from "lucide-react";
// Live + hardcoded card data — the SAME sources the category pages render, so
// anything visible on the site (including admin-added restaurants) is searchable.
import { useAllRestaurantPlaces, toSlug } from "./Restaurants";
import { TEMPLES } from "./Temples";
import { VENUES } from "./Venues";
import { DECORATORS } from "./Decorators";
import { MOVIES_DATA } from "./Movies";
import { GROCERY_STORES } from "./Grocery";
import { SHOPPING_STORES } from "./Shopping";
import { EVENTS } from "./Events";
import { CRICKET_ENTRIES } from "./Cricket";
import { HEALTH_PROVIDERS } from "./HealthWellness";
import { PERFORMING_ARTISTS_DATA } from "./PerformingArtists";
import { NONPROFIT_ENTRIES } from "./NonProfitOrg";
import { PHOTO_VIDEO_ENTRIES } from "./PhotoVideo";
import { FINANCIAL_SERVICE_ENTRIES } from "./FinancialServices";
import { DESSERTS_DATA } from "./Desserts";
import { REALTOR_ENTRIES } from "./Realtors";
import { EVENT_ORGANIZERS } from "./EventOrganizer";
import { SALON_SPA_STORES } from "./SalonSpa";
import { MAKEUP_ARTISTS } from "./MakeupHair";
import { DJS } from "./DJs";
import { MEDIA_ITEMS } from "./Media";
import { KIDS_ENTRIES } from "./Kids";
import { CATERERS } from "./Catering";
import { SENIOR_ENTRIES } from "./SeniorCitizen";
import { YOUTH_ENTRIES } from "./Youth";
import { LAWYER_ENTRIES } from "./Lawyers";
import { HOME_SERVICES_ENTRIES } from "./HomeServices";
import { AUTOMOTIVE_ENTRIES } from "./AutomotiveServices";
import { CONSULTING_ENTRIES } from "./Consulting";

interface SearchItem {
  title: string;
  category: string;
  location?: string;
  description?: string;
  href: string;
}

// Category entries — let users jump straight to a category page by name/keyword.
const CATEGORY_ENTRIES: SearchItem[] = [
  { title: "Restaurants", category: "Category", description: "Top rated Indian restaurants in Dallas-DFW", href: "/restaurants" },
  { title: "Events", category: "Category", description: "Upcoming events & happenings", href: "/events" },
  { title: "Concerts/Events", category: "Category", description: "Live music & concerts", href: "/concerts" },
  { title: "Temples/Spiritual Services", category: "Category", description: "Temples & religious places", href: "/temples" },
  { title: "Shopping", category: "Category", description: "Indian & desi shopping", href: "/shopping" },
  { title: "Grocery", category: "Category", description: "Indian grocery stores", href: "/grocery" },
  { title: "Movies", category: "Category", description: "Indian movies & showtimes", href: "/movies" },
  { title: "Party Venues", category: "Category", description: "Event & wedding venues", href: "/party-venues" },
  { title: "Event Management", category: "Category", description: "Event & wedding decorators", href: "/event-management" },
  { title: "Influencers", category: "Category", description: "DFW desi influencers", href: "/influencers" },
  { title: "Models", category: "Category", description: "DFW desi models & talent", href: "/models" },
  { title: "Kids Fun", category: "Category", description: "Kids activities & fun", href: "/kidsfun" },
  { title: "Experiences", category: "Category", description: "Community experiences & reviews", href: "/experiences" },
  { title: "Cricket", category: "Category", description: "Cricket leagues & clubs in Dallas-DFW", href: "/cricket" },
  { title: "Add Business", category: "Action", description: "List your business on DallasDesiScoop", href: "/add-business" },
  { title: "All Categories", category: "Page", description: "Browse all categories", href: "/categories" },
];

// Normalize: lowercase, strip punctuation, collapse whitespace — so "Dr. Lavanya"
// matches "dr lavanya", and "soul-food" matches "Soul Food".
function normalize(s: string): string {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Pull every searchable string out of a card object (shallow walk): name,
// description, cuisines, tags, address, phone, services — whatever fields
// exist on that category's cards. This keeps the matcher generic across all
// categories without hardcoding a per-category field list.
function cardSearchText(card: any): string {
  const parts: string[] = [];
  for (const v of Object.values(card || {})) {
    if (typeof v === "string" || typeof v === "number") parts.push(String(v));
    else if (Array.isArray(v)) {
      for (const av of v) {
        if (typeof av === "string" || typeof av === "number") parts.push(String(av));
        else if (av && typeof av === "object")
          for (const sv of Object.values(av)) {
            if (typeof sv === "string" || typeof sv === "number") parts.push(String(sv));
          }
      }
    }
  }
  return normalize(parts.join(" "));
}

function pick(card: any, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = card?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v) && v.length && typeof v[0] === "string") return v.join(", ");
  }
  return undefined;
}

// Build one SearchItem per card in a category.
function toItems(cards: any[], category: string, href: string, descKeys: string[] = ["description", "about", "specialty", "services", "cuisines", "tags", "type", "category"]): SearchItem[] {
  return (cards || []).map((c) => ({
    title: String(c?.name || c?.title || c?.businessName || "Untitled"),
    category,
    location: pick(c, ["address", "city", "location", "area", "neighborhood"]),
    description: pick(c, descKeys),
    href,
  }));
}

// Static (hardcoded-default) card index, built once. Admin-added cards for
// non-restaurant categories are overrides/edits of these defaults and are
// covered by the same fields; restaurants (the live, fully dynamic source)
// are indexed separately below via useAllRestaurantPlaces.
const CARD_INDEX: SearchItem[] = [
  ...toItems(TEMPLES, "Temple", "/temples"),
  ...toItems(VENUES, "Party Venue", "/party-venues"),
  ...toItems(DECORATORS, "Event Management", "/event-management"),
  ...toItems(MOVIES_DATA, "Movie", "/movies"),
  ...toItems(GROCERY_STORES, "Grocery Store", "/grocery"),
  ...toItems(SHOPPING_STORES, "Shopping", "/shopping"),
  ...toItems(EVENTS, "Event", "/events"),
  ...toItems(CRICKET_ENTRIES, "Cricket", "/cricket"),
  ...toItems(HEALTH_PROVIDERS, "Health & Wellness", "/health-wellness"),
  ...toItems(PERFORMING_ARTISTS_DATA, "Performing Artist", "/performing-artists"),
  ...toItems(NONPROFIT_ENTRIES, "Non Profit Org", "/non-profit-org"),
  ...toItems(PHOTO_VIDEO_ENTRIES, "Photo/Video", "/photo-video"),
  ...toItems(FINANCIAL_SERVICE_ENTRIES, "Financial Services", "/financial-services"),
  ...toItems(DESSERTS_DATA, "Dessert Shop", "/dessert-shops"),
  ...toItems(REALTOR_ENTRIES, "Realtor", "/realtors"),
  ...toItems(EVENT_ORGANIZERS, "Event Production", "/event-production"),
  ...toItems(SALON_SPA_STORES, "Salon/Spa", "/salon-spa"),
  ...toItems(MAKEUP_ARTISTS, "Beauty Services", "/BeautyServices"),
  ...toItems(DJS, "DJ", "/djs"),
  ...toItems(MEDIA_ITEMS, "Media", "/media"),
  ...toItems(KIDS_ENTRIES, "Kids", "/kids"),
  ...toItems(CATERERS, "Catering", "/catering-only"),
  ...toItems(SENIOR_ENTRIES, "Senior Citizens", "/senior-citizen"),
  ...toItems(YOUTH_ENTRIES, "Youth", "/youth"),
  ...toItems(LAWYER_ENTRIES, "Lawyer", "/lawyers"),
  ...toItems(HOME_SERVICES_ENTRIES, "Home Services", "/home-services"),
  ...toItems(AUTOMOTIVE_ENTRIES, "Automotive Services", "/automotive-services"),
  ...toItems(CONSULTING_ENTRIES, "Consulting", "/consulting"),
];

// Pre-normalized text per card for fast matching.
const CARD_TEXTS = CARD_INDEX.map((c) => ({
  item: c,
  text: normalize(`${c.title} ${c.category} ${c.location || ""} ${c.description || ""}`),
  title: normalize(c.title),
}));

interface Scored {
  item: SearchItem;
  score: number;
}

// Match ANY word of the query (per the owner's ask: "any word or sentence").
// Cards matching MORE words (or the full phrase, or the title) rank higher.
function scoreMatch(text: string, title: string, words: string[], phrase: string): number {
  let score = 0;
  let matched = 0;
  for (const w of words) {
    if (text.includes(w)) matched++;
  }
  if (matched === 0) return 0;
  score = matched * 2;
  if (phrase && text.includes(phrase)) score += 5;
  if (words.some((w) => title.includes(w))) score += 3;
  return score;
}

function SearchResults({ query, onClose }: { query: string; onClose: () => void }) {
  const navigate = useNavigate();
  // Live restaurant source — same one the Restaurants page uses, so
  // admin-added restaurants are searchable immediately.
  const { items: places, loading: placesLoading } = useAllRestaurantPlaces();

  const restaurantIndex = useMemo(
    () =>
      (places || []).map((p: any) => {
        const title = String(p?.name || "Untitled");
        const city = p?.city || p?.location || "";
        const href = `/restaurants/${toSlug(title)}-${toSlug(city || "")}`.replace(/-+$/, "");
        return {
          item: {
            title,
            category: "Restaurant",
            location: pick(p, ["address", "city", "location"]),
            description: pick(p, ["cuisines", "cuisine", "category", "type"]),
            href,
          } as SearchItem,
          text: cardSearchText(p),
          title: normalize(title),
        };
      }),
    [places]
  );

  const results = useMemo<Scored[]>(() => {
    const phrase = normalize(query);
    if (!phrase) return [];
    const words = phrase.split(" ").filter(Boolean);
    const out: Scored[] = [];

    // Category entries (title/keyword match).
    for (const c of CATEGORY_ENTRIES) {
      const text = normalize(`${c.title} ${c.category} ${c.description || ""}`);
      const s = scoreMatch(text, normalize(c.title), words, phrase);
      if (s > 0) out.push({ item: c, score: s + 1 }); // slight boost: direct navigation
    }
    // All cards from every category.
    for (const c of CARD_TEXTS) {
      const s = scoreMatch(c.text, c.title, words, phrase);
      if (s > 0) out.push({ item: c.item, score: s });
    }
    // Live restaurants.
    for (const r of restaurantIndex) {
      const s = scoreMatch(r.text, r.title, words, phrase);
      if (s > 0) out.push({ item: r.item, score: s });
    }

    out.sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title));
    return out;
  }, [query, restaurantIndex]);

  function handleSelect(href: string) {
    navigate(href);
    onClose();
  }

  if (query.trim() === "") {
    return (
      <div className="px-5 py-8 text-center">
        <p className="text-[13px] text-gray-400">Type to search across all categories</p>
      </div>
    );
  }

  if (results.length === 0 && placesLoading) {
    return (
      <div className="px-5 py-8 text-center">
        <p className="text-[13px] text-gray-400">Searching…</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-[14px] font-semibold text-gray-700">No results found.</p>
        <p className="text-[12px] text-gray-400 mt-1">Try a different keyword.</p>
      </div>
    );
  }

  const shown = results.slice(0, 40);

  return (
    <div className="py-2">
      {placesLoading && results.length > 0 && (
        <p className="px-5 pb-1 text-[11px] text-gray-400">Loading more restaurant results…</p>
      )}
      {shown.map((r, i) => (
        <button
          key={`${r.item.href}-${r.item.title}-${i}`}
          onClick={() => handleSelect(r.item.href)}
          className="w-full text-left px-5 py-3 flex items-start gap-3 hover:bg-orange-50/60 transition-colors duration-150 group"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold text-gray-900 group-hover:text-[#f97316] transition-colors truncate">
                {r.item.title}
              </span>
              <span className="text-[10px] font-semibold text-[#f97316] bg-orange-50 rounded-full px-2 py-0.5 shrink-0">
                {r.item.category}
              </span>
            </div>
            {r.item.location && (
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="text-[11px] text-gray-500 truncate">{r.item.location}</span>
              </div>
            )}
            {r.item.description && (
              <p className="text-[12px] text-gray-400 mt-0.5 truncate">{r.item.description}</p>
            )}
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#f97316] shrink-0 mt-1 transition-colors" />
        </button>
      ))}
      {results.length > shown.length && (
        <p className="px-5 py-2 text-[11px] text-gray-400">
          Showing {shown.length} of {results.length} matches — keep typing to narrow down.
        </p>
      )}
    </div>
  );
}

export default function GlobalSearchModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    function h(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (isOpen) {
      document.addEventListener("keydown", h);
      return () => document.removeEventListener("keydown", h);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[99999] flex items-start justify-center pt-[15vh]"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full max-w-[560px] mx-4 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            style={{ boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)" }}
          >
            {/* Search Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <Search className="w-5 h-5 text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search restaurants, events, party venues…"
                className="flex-1 text-[15px] text-gray-800 placeholder:text-gray-400 outline-none bg-transparent"
                autoComplete="off"
              />
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Results — mounted only while open so the live restaurant fetch
                doesn't fire on every page load. */}
            <div className="max-h-[340px] overflow-y-auto">
              <SearchResults query={query} onClose={onClose} />
            </div>

            {/* Footer hint */}
            <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-2">
              <span className="text-[10px] text-gray-400">Press</span>
              <kbd className="text-[10px] font-semibold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">ESC</kbd>
              <span className="text-[10px] text-gray-400">to close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
